import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Shield, Scale, User } from 'lucide-react';

const HomeownerBenefitsSection = () => {
  const benefits = [
    {
      id: 1,
      icon: Eye,
      text: "You know what you're paying for. No bundled numbers. No guessing. Every dollar has a reason."
    },
    {
      id: 2,
      icon: Shield,
      text: "You get pricing without pressure. No sales tactics, no 'today-only' discounts, no rushed decisions."
    },
    {
      id: 3,
      icon: Scale,
      text: "You get fair pricing by design. The same roof shouldn\'t cost thousands more just because you called the wrong company."
    },
    {
      id: 4,
      icon: User,
      text: "You stay in control from start to finish. You choose materials, timing, and next steps — not a salesperson."
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardHover = {
    rest: { y: 0, scale: 1 },
    hover: { y: -6, scale: 1.02, transition: { duration: 0.3, ease: "easeOut" } }
  };

  return (
    <section className="relative py-16 lg:py-20 bg-gradient-to-b from-slate-50/50 via-slate-100/30 to-white overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-80 h-80 bg-cyan-100/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-blue-100/10 rounded-full blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Label */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="text-center mb-6"
        >
          <span className="inline-block text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 uppercase tracking-[0.25em] px-4 py-2 rounded-full border border-cyan-200/50 bg-white/50 backdrop-blur-sm">
            HOW THIS BENEFITS HOMEOWNERS
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="text-4xl lg:text-5xl xl:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-center mb-20 leading-tight"
        >
          What this means for you.
        </motion.h2>

        {/* Benefit Cards - 2x2 Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16 max-w-6xl mx-auto"
        >
          {benefits?.map((benefit) => {
            const IconComponent = benefit?.icon;
            return (
              <motion.div
                key={benefit?.id}
                variants={fadeInUp}
                initial="rest"
                whileHover="hover"
                animate="rest"
                className="group"
              >
                <motion.div
                  variants={cardHover}
                  className="relative h-full bg-white border border-slate-200/80 rounded-2xl p-8 lg:p-10 shadow-md hover:shadow-xl hover:border-cyan-300/60 transition-all duration-300 overflow-hidden"
                >
                  {/* Subtle gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/0 via-blue-50/0 to-cyan-50/0 group-hover:from-cyan-50/40 group-hover:via-blue-50/20 group-hover:to-cyan-50/40 transition-all duration-500 rounded-2xl" />

                  {/* Icon */}
                  <div className="relative mb-6 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20 group-hover:shadow-xl group-hover:shadow-cyan-500/30 group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="w-7 h-7" strokeWidth={2.5} />
                  </div>

                  {/* Benefit Text */}
                  <p className="relative z-10 text-base lg:text-lg text-slate-700 leading-relaxed font-medium group-hover:text-slate-900 transition-colors duration-300">
                    {benefit?.text}
                  </p>

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Closing Line */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="text-center"
        >
          <p className="text-xl lg:text-2xl font-semibold text-slate-700 max-w-3xl mx-auto">
            Roofing should feel clear, calm, and predictable. That's the point.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeownerBenefitsSection;